import { useFieldArray, useFormContext } from 'react-hook-form';
import { RadioButtonGroup, TextFieldElement } from 'react-hook-form-mui';
import {
    Button,
    Card,
    CardContent,
    Typography,
    Stack,
    IconButton,
    styled,
    Modal,
    InputAdornment,
    Divider,
    Box,
    FormLabel,
} from '@mui/material';
import { Delete, Add } from '@mui/icons-material';
import { generateDiscountString, validateSingleTier, validateTiersArray } from '@/utils/helpers/discountHelper';
import RHFDateRangePicker from './RHFDateRangePicker';
import { IDealForm } from '@/utils/zod/dealFormValidation';
import FormErrorDisplayer from './ErrorMessageDisplayer';

const StyledCard = styled(Card)(({ theme }) => ({
    maxWidth: 800,
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    padding: '16px',
    maxHeight: '90vh',
    overflow: 'auto',
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[24],
}));

const FinalPriceText = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    marginLeft: theme.spacing(9)
}));

// Define a type for field labels
type FieldLabels = {
    [key: string]: {
        // quantity: string;
        connector: string;
        value: string;
        unit: string;
        type: string;
        start_date: string;
        end_date: string;
    };
};

// Update FIELD_LABELS to use the new type
const FIELD_LABELS: FieldLabels = {
    // percentage: { quantity: 'Buy (quantity)', connector: 'items & get', value: 'Get % Off', unit: '%' },
    // fixed: { quantity: 'Buy (quantity)', connector: 'items & get', value: 'Amount Off', unit: '$' },
    // bundle: { quantity: 'Buy (quantity)', connector: 'items at a Bundle Price of', value: 'Price', unit: '$' },
    product_creation_based: {
        start_date: 'Start Date',
        connector: 'Products Launched Between',
        end_date: 'End Date',
        value: 'Discount Percentage',
        type: 'percentage',
        unit: '%'
    },
};

// Define a type for the dynamic name format
type DiscountName = `tag_rules.${number}.discount`;

interface DiscountFormProps {
    open: boolean;
    onClose: () => void;
    name: DiscountName; // Use the template literal type
    discountType: 'product_creation_based';
}

const DiscountForm = ({ open = false, name, onClose, discountType }: DiscountFormProps) => {
    const formContext = useFormContext<IDealForm>();
    const { control, setValue, watch } = formContext;
    const tiers = watch(`${name}.${discountType}_tiers`) || [];

    const fieldArray = useFieldArray({
        control,
        name: `${name}.product_creation_based_tiers`
    });

    const fieldLabels = FIELD_LABELS[discountType as keyof typeof FIELD_LABELS];

    const addTier = () => {
        // const lastTier = tiers[tiers.length - 1];
        // const nextQuantity = lastTier?.quantity > 0 ? lastTier.quantity + 1 : 1;
        if (validateTiersArray(fieldArray.fields)) {
            fieldArray.append({ start_date: null, end_date: null, value: 0, type: 'percentage' });
        }
    }

    const handleSave = () => {
        const discountString = generateDiscountString(
            tiers.map((tier, index) => ({
                ...tier,
                id: fieldArray.fields[index].id
            })),
            discountType
        );

        // Ensure the discount string is not empty
        if (!discountString) {
            alert("Discount string cannot be empty. Please add valid tiers.");
            return;
        }

        // Debugging: Log the value being set
        console.log(`Setting discount_string for ${name}:`, discountString);

        // Ensure the path is correct
        setValue(`${name}.discount_string`, discountString);
        onClose();
    };
    
    return (
        <Modal
            open={open}
            onClose={onClose}
            aria-labelledby="discount-form-modal"
        >
            <StyledCard>
                <CardContent>
                    <Stack spacing={3}>
                        <Box>
                            <FormLabel sx={{ mb: 2, display: 'block' }}>
                                Discount Type
                            </FormLabel>
                            <RadioButtonGroup
                                name={`${name}.discount_type`}
                                options={[
                                    // { id: 'percentage', label: 'Percentage Off' },
                                    // { id: 'fixed', label: 'Fixed Amount Off' },
                                    // { id: 'bundle', label: 'Bundle Deal' },
                                    { id: 'product_creation_based', label: 'Product Creation Date Based Discount' }
                                ]}
                                row
                            />
                        </Box>

                        <Divider />

                        <Typography variant="subtitle1">Discount Tiers</Typography>

                        <Stack spacing={2}>
                            {fieldArray.fields.map((field, index) => {
                                return (
                                    <Card variant="outlined" key={field.id}>
                                        <CardContent>
                                            <Stack direction="row" spacing={2} alignItems="center">
                                                <Stack direction="row" spacing={2} alignItems="center">
                                                    <RHFDateRangePicker
                                                        name={`${name}.product_creation_based_tiers.${index}`}
                                                        size="small"
                                                    />
                                                    <TextFieldElement
                                                        name={`${name}.product_creation_based_tiers.${index}.value`}
                                                        label={fieldLabels.value}
                                                        type="number"
                                                        slotProps={{
                                                            input: {
                                                                endAdornment: <InputAdornment position="end">%</InputAdornment>
                                                            }
                                                        }}
                                                        size="small"
                                                    />
                                                </Stack>
                                                <IconButton onClick={() => fieldArray.remove(index)} color="error">
                                                    <Delete />
                                                </IconButton>
                                            </Stack>
                                        </CardContent>
                                    </Card>
                                );
                            })}
                        </Stack>

                        <FormErrorDisplayer />
                        {tiers.some((field) => validateSingleTier(field).isValid) && (
                            <FinalPriceText variant="caption">
                                Final Discount = {generateDiscountString(tiers, discountType)}
                            </FinalPriceText>
                        )}

                        <Button
                            startIcon={<Add />}
                            variant="outlined"
                            onClick={addTier}
                            fullWidth
                            disabled={!validateTiersArray(fieldArray.fields)}
                        >
                            Add Tier
                        </Button>

                        <Stack direction="row" spacing={2} justifyContent="flex-end">
                            <Button
                                variant="contained"
                                onClick={handleSave}
                                disabled={!validateTiersArray(fieldArray.fields)}
                            >
                                Save Discount
                            </Button>
                        </Stack>
                    </Stack>
                </CardContent>
            </StyledCard>
        </Modal>
    );
};

export default DiscountForm;
